import React, { useState, useEffect, useCallback } from 'react';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import { ReportDetails } from './ReportDetails';
import { MarkerSearchView } from './MarkerSearchView';
import { apiService } from '../services/apiService';

interface LabReport {
  id: number;
  filename: string;
  testDate: string | null;
  uploadDate: string;
  resultCount: number;
}

interface ReportsManagerProps {
  onClose?: () => void;
}

export const ReportsManager: React.FC<ReportsManagerProps> = ({ onClose }) => {
  const { user } = useSimpleAuth();
  const [reports, setReports] = useState<LabReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [showMarkerSearch, setShowMarkerSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [markerSearchTerm, setMarkerSearchTerm] = useState('');

  // Load reports
  const loadReports = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getReports();

      if (response.status === 'success') {
        setReports(response.data);
      }
    } catch (error: any) {
      console.error('Error loading reports:', error);
      setError(error.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Filter and sort reports
  const filteredReports = reports
    .filter(report => 
      report.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (report.testDate && report.testDate.includes(searchTerm))
    )
    .sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'date') {
        const dateA = new Date(a.testDate || a.uploadDate);
        const dateB = new Date(b.testDate || b.uploadDate);
        comparison = dateA.getTime() - dateB.getTime();
      } else {
        comparison = a.filename.localeCompare(b.filename);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Delete report
  const handleDeleteReport = async (reportId: number) => {
    if (!window.confirm('Are you sure you want to delete this report and all its data?')) {
      return;
    }

    try {
      const response = await apiService.deleteReport(reportId);

      if (response.status === 'success') {
        setReports(reports.filter(r => r.id !== reportId));
        if (selectedReportId === reportId) {
          setSelectedReportId(null);
        }
      }
    } catch (error: any) {
      console.error('Error deleting report:', error);
      alert(error.message || 'Failed to delete report');
    }
  };

  // If a report is selected, show the details view
  if (selectedReportId) {
    return (
      <ReportDetails
        reportId={selectedReportId}
        onBack={() => setSelectedReportId(null)}
        onUpdate={loadReports}
      />
    );
  }

  // If marker search is active, show the search view with the search term
  if (showMarkerSearch) {
    return (
      <MarkerSearchView
        onBack={() => {
          setShowMarkerSearch(false);
          setMarkerSearchTerm('');
        }}
        initialSearchTerm={markerSearchTerm}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Lab Reports Manager</h1>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
                title="Back to Dashboard"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Back</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search by filename, date, or marker..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && searchTerm.trim()) {
                    // Check if this looks like a marker search (contains letters and doesn't look like a filename)
                    const isMarkerSearch = /^[a-zA-Z\s]+$/.test(searchTerm.trim()) && 
                                         !searchTerm.toLowerCase().includes('.pdf') &&
                                         !searchTerm.match(/\d{4}-\d{2}-\d{2}/);
                    
                    if (isMarkerSearch) {
                      setMarkerSearchTerm(searchTerm);
                      setShowMarkerSearch(true);
                    }
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                Press Enter to search markers
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'name')}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500"
                title={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading reports...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Reports list */}
        {!loading && !error && (
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            {filteredReports.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {searchTerm ? 'No reports match your search' : 'No reports uploaded yet'}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredReports.map((report) => (
                  <div
                    key={report.id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">
                          {report.filename}
                        </h3>
                        <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                          <span>
                            Test Date: {report.testDate 
                              ? new Date(report.testDate).toLocaleDateString()
                              : 'Not specified'}
                          </span>
                          <span>•</span>
                          <span>
                            Uploaded: {new Date(report.uploadDate).toLocaleDateString()}
                          </span>
                          <span>•</span>
                          <span>{report.resultCount} results</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedReportId(report.id)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          View & Edit
                        </button>
                        <button
                          onClick={() => handleDeleteReport(report.id)}
                          className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete report"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary stats */}
        {!loading && !error && reports.length > 0 && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-500">Total Reports</div>
              <div className="text-2xl font-bold text-gray-900">{reports.length}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-500">Total Results</div>
              <div className="text-2xl font-bold text-gray-900">
                {reports.reduce((sum, r) => sum + r.resultCount, 0)}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-500">Date Range</div>
              <div className="text-sm font-medium text-gray-900">
                {reports.length > 0 && (
                  <>
                    {new Date(Math.min(...reports
                      .filter(r => r.testDate)
                      .map(r => new Date(r.testDate!).getTime())
                    )).toLocaleDateString()}
                    {' - '}
                    {new Date(Math.max(...reports
                      .filter(r => r.testDate)
                      .map(r => new Date(r.testDate!).getTime())
                    )).toLocaleDateString()}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsManager;